import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from 'app-config';
import { HttpClient } from '@angular/common/http';
import {
  AlertService,
  ChainRepository,
  SignerRepository,
  SignerService,
  TradeSateEnum,
} from 'common';
import {
  ChainSettingsState,
  EnzymeRepository,
  EnzymeState,
  RebalancingRepository,
  RebalancingState,
  SettingsRepository,
  TransactionExecutionRepository,
} from '../repository';
import { CalculationProgress, Rebalancing } from '../model';
import { lastValueFrom } from 'rxjs';
import { Address, sleep } from '@31third/common';
import { BatchTradeService } from './batch-trade.service';
import { TradeService } from './trade.service';
import {
  RebalancingAllocationEntryFactory,
  RebalancingAmountEntryFactory,
  RebalancingFactory,
} from '../factory';
import {
  BaseRebalancingQueryDto,
  CalculationProgressDto,
  RebalancingEnzymeVaultQueryDto,
  RebalancingPausedInfoDto,
  RebalancingResponseDto,
  RebalancingWalletQueryDto,
  SwapResponseDto,
} from '../dto';
import { RebalancingStep } from '../component';
import { RebalancingType } from '../enum';
import { SentryService } from './sentry.service';
import { RebalancingErrorTrackerService } from './rebalancing-error-tracker.service';

@Injectable({
  providedIn: 'root',
})
export class RebalancingService {
  public rebalancingCanceled = false;

  constructor(
    @Inject(APP_CONFIG) private appConfig: any,
    private readonly httpClient: HttpClient,
    private readonly signerService: SignerService,
    private readonly chainRepository: ChainRepository,
    private readonly alertService: AlertService,
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly signerRepository: SignerRepository,
    private readonly settingsRepository: SettingsRepository,
    private readonly enzymeRepository: EnzymeRepository,
    private readonly batchTradeService: BatchTradeService,
    private readonly tradeService: TradeService,
    private readonly sentryService: SentryService,
    private readonly transactionExecutionRepository: TransactionExecutionRepository,
    private readonly rebalancingErrorTrackerService: RebalancingErrorTrackerService,
  ) {}

  private getBaseUrl(): string {
    return `${this.appConfig.tradingApiBaseUrl}/rebalancing`;
  }

  public get rebalancingState(): RebalancingState {
    return this.rebalancingRepository.store.getValue();
  }

  public get enzymeState(): EnzymeState {
    return this.enzymeRepository.store.getValue();
  }

  // TODO: make abstract and impl own logic for enzyme/set ...
  // TODO: enzyme repo can be moved back to trading app instead of common-reb lib
  public async calculateRebalancing() {
    try {
      let rebalancing: Rebalancing | undefined;

      if (
        this.settingsRepository.getActiveChainSetting()?.useSwap &&
        this.rebalancingRepository.isSwap()
      ) {
        // just for testing purpose
        rebalancing = await this.getSwap();
      } else {
        rebalancing = await this.calculateRebalancingAsyncAndTrackProgress();
      }

      if (rebalancing) {
        this.rebalancingRepository.setRebalancing(rebalancing);
      }
    } catch (e: any) {
      this.rebalancingRepository.setStep(RebalancingStep.INPUT_OK);
      const storeValue = this.settingsRepository.getActiveChainSetting();
      if (
        storeValue?.automatedMarketMakerEnabled &&
        storeValue?.excludedExchanges &&
        storeValue?.excludedExchanges.length > 0
      ) {
        this.alertService.showError(
          'rebalancing.error.calculationExchangeList',
          undefined,
          false,
          10000,
        );
      } else if (e.message) {
        this.alertService.showError(e.message);
      } else {
        this.alertService.showError('rebalancing.error.calculation');
      }
    }
  }

  private async calculateRebalancingAsyncAndTrackProgress(): Promise<
    Rebalancing | undefined
  > {
    this.rebalancingCanceled = false;

    try {
      const rebalancingResponseDto = await lastValueFrom(
        this.httpClient.post<RebalancingResponseDto>(
          this.getRebalancingUrl(),
          this.createRebalancingQuery(),
        ),
      );

      this.rebalancingRepository.setCalculatingRebalancingId(
        rebalancingResponseDto.id,
      );
      return this.waitForRebalancingToFinish(rebalancingResponseDto.id);
    } catch (e) {
      this.rebalancingErrorTrackerService.recordError();
      throw e;
    }

    // TODO: implement set repo
    // if (this.rebalancingState.activeVaultType !== VaultType.SET_PROTOCOL) {
    //   return this.waitForRebalancingToFinish(rebalancing);
    // } else {
    //   // TODO: implement async rebalancing for setProtocol
    //   return SetProtocolRebalancingFactory.updateFromDto(
    //     rebalancing,
    //     rebalancingResponseDto as SetProtocolRebalancingResponseDto,
    //     rebalancingResponseDto.requiredAllowances?.map((allowance, index) =>
    //       AllowanceFactory.createFromDto(allowance, index),
    //     ),
    //     rebalancingResponseDto.trades.map(tradeDto =>
    //       SetProtocolTradeFactory.createFromDto(
    //         tradeDto as SetProtocolTradeDto,
    //         TokenFactory.createTokenFromDto(tradeDto.from),
    //         TokenFactory.createTokenFromDto(tradeDto.to),
    //       ),
    //     ),
    //   );
    // }
  }

  private getRebalancingUrl(): string {
    if (this.rebalancingState.type === RebalancingType.WALLET) {
      return `${this.getBaseUrl()}/wallet`;
    } else {
      if (this.enzymeState.selectedVault) {
        return `${this.getBaseUrl()}/enzyme`;
      } else {
        throw new Error('SetProtocol currently not supported');
      }
    }
  }

  private createRebalancingQuery(): BaseRebalancingQueryDto {
    const signerAddress = this.signerRepository.store.getValue().address;
    if (!signerAddress) {
      throw new Error('No signer connected');
    }

    let query: RebalancingWalletQueryDto | RebalancingEnzymeVaultQueryDto;
    const settings: ChainSettingsState | undefined =
      this.settingsRepository.getActiveChainSetting();

    if (this.rebalancingState.type === RebalancingType.WALLET) {
      query = new RebalancingWalletQueryDto();
      query.wallet = signerAddress;
    } else {
      if (this.enzymeState.selectedVault) {
        query = new RebalancingEnzymeVaultQueryDto();
        query.vault = this.enzymeState.selectedVault.address;
        query.batchTrade = false;
        query.testnet = settings?.enzymeTestnet;
        if (this.enzymeState.useOwnerInsteadOfConnectedWallet) {
          query.simulationTxOrigin = this.enzymeState.selectedVault.owner;
        }

        // TODO: add type for Enzyme here
      } else {
        throw new Error('SetProtocol currently not supported');
        // TODO: implement set repo
        // query['setToken'] =
        //   this.settingsRepository.getActiveChainSetting()?.setProtocolAddress;
        // query.batchTrade = false;
        // updateFromDto = SetProtocolRebalancingFactory.updateFromDto;
      }
    }

    const baseEntries = this.rebalancingState.sellEntries
      .filter(entry => !entry.excludeFromRebalancing)
      .map(entry =>
        RebalancingAmountEntryFactory.createAmountEntryFromSellEntry(entry),
      );
    const targetEntries = this.rebalancingState.buyEntries.map(entry =>
      RebalancingAllocationEntryFactory.createAllocationEntryFromSellEntry(
        entry,
      ),
    );

    query.signer = signerAddress;
    query.baseEntries = baseEntries;
    query.targetEntries = targetEntries;
    query.batchTrade = settings?.batchTrade;
    query.revertOnError = settings?.revertOnError;
    query.maxSlippage =
      settings?.maxSlippage !== undefined && !isNaN(settings?.maxSlippage)
        ? settings?.maxSlippage
        : undefined;
    query.maxPriceImpact =
      settings?.maxPriceImpact !== undefined && !isNaN(settings?.maxPriceImpact)
        ? settings?.maxPriceImpact
        : undefined;
    query.minExpirySec =
      settings?.minExpirySec !== undefined && !isNaN(settings?.minExpirySec)
        ? settings?.minExpirySec
        : undefined;
    query.minTradeValue =
      settings?.minTradeValue !== undefined && !isNaN(settings?.minTradeValue)
        ? settings?.minTradeValue
        : undefined;
    query.async = true;

    if (settings?.liquiditySourceConfig) {
      query.liquiditySourceConfig = settings.liquiditySourceConfig.getDto();
    }

    return query;
  }

  public async waitForRebalancingToFinish(
    id: string,
  ): Promise<Rebalancing | undefined> {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (this.rebalancingCanceled) {
        return;
      }
      await sleep(1000);
      if (this.rebalancingCanceled) {
        return;
      }

      try {
        const calculationProgressDto: CalculationProgressDto =
          await lastValueFrom(
            this.httpClient.get<CalculationProgressDto>(
              `${this.getBaseUrl()}/calculation-progress/${id}`,
            ),
          );
        if (this.rebalancingCanceled) {
          return undefined;
        }
        if (calculationProgressDto.failed) {
          throw Error(calculationProgressDto.message);
        } else {
          this.rebalancingRepository.setCalculationProgress(
            new CalculationProgress(
              calculationProgressDto.percent,
              calculationProgressDto.message,
            ),
          );
          if (calculationProgressDto.rebalancing) {
            this.rebalancingErrorTrackerService.recordSuccess();

            return RebalancingFactory.updateFromDto(
              new Rebalancing(),
              calculationProgressDto.rebalancing,
            );
          }
        }
      } catch (e) {
        this.rebalancingErrorTrackerService.recordError();
        throw e;
      }
    }
    // TODO: integrate EventSource listener
    // let source = new EventSource(
    //   `${this.TRADING_API_BASE_URL}/rebalancing/calculation-progress/${rebalancingId}`,
    //   {
    //     headers: {
    //       'X-Api-Key': environment.tradingAPIKey,
    //     },
    //   },
    // );
    //
    // source.onmessage = (message: { data: string }) => {
    // finished.next(true);
    // };
    //
    // source.onerror = (event: any) => {
    //   finished.next(true);
    // };
    // return new Promise<void>(resolve => {
    //   finished.asObservable().subscribe(value => {
    //     if (value) {
    //       source.close();
    //       resolve();
    //     }
    //   });
    // });
  }

  public async executeRebalancing(): Promise<void> {
    const signer = this.signerService.getSigner();
    if (!signer) {
      throw new Error('Wallet not connected anymore');
    }

    const rebalancing = this.rebalancingRepository.store.getValue().rebalancing;
    if (!rebalancing) {
      throw new Error('No rebalancing calculated');
    }

    this.transactionExecutionRepository.startExecution(false);
    this.sentryService.captureMsg('executeRebalancing() called');

    // reset failed state
    rebalancing.trades.forEach(trade => {
      if (trade.tradeState === TradeSateEnum.FAILED) {
        this.rebalancingRepository.updateTrade(trade.id, {
          tradeState: TradeSateEnum.READY,
        });
      }
    });

    this.rebalancingRepository.setStep(RebalancingStep.IN_PROGRESS);
    const chain = this.chainRepository.store.getValue().chain;
    const batchTrade: boolean =
      (!chain || chain.batchTradeSupported) &&
      this.settingsRepository.getActiveChainSetting()?.batchTrade === true;
    if (
      batchTrade
      // TODO: refactor this. currently vaults also get rebalanced by batch trade serivce
      // this.rebalancingRepository.store.getValue().activeVaultType === undefined
    ) {
      return this.batchTradeService.executeRebalancing(rebalancing);
    } else {
      return this.tradeService.processTrades();
    }
  }

  public async checkPaused(): Promise<void> {
    const pausedInfoDto = await lastValueFrom(
      this.httpClient.get<RebalancingPausedInfoDto>(
        `${this.getBaseUrl()}/is-paused`,
      ),
    );
    this.rebalancingRepository.setTradingPaused(
      pausedInfoDto.paused,
      pausedInfoDto.pausedInfo,
      pausedInfoDto.capacityReached,
      pausedInfoDto.capacityReachedInfo,
    );
  }

  public async cancelRebalancing(): Promise<void> {
    this.rebalancingCanceled = true;
    const id = this.rebalancingState.calculatingRebalancingId;
    await lastValueFrom(
      this.httpClient.get<CalculationProgressDto>(
        `${this.getBaseUrl()}/cancel/${id}`,
      ),
    );
  }

  /**
   * Just or testing purpose
   * @private
   */
  private async getSwap(): Promise<Rebalancing | undefined> {
    const signerAddress = this.signerRepository.store.getValue().address;
    if (!signerAddress) {
      throw new Error('No signer connected');
    }

    const sellEntry = this.rebalancingState.sellEntries.filter(
      entry => !entry.excludeFromRebalancing,
    )[0];
    const buyEntry = this.rebalancingState.buyEntries[0];

    let taker: Address = signerAddress;
    let txOrigin: Address = signerAddress;
    let encodingType: 'basic' | 'enzyme-vault' = 'basic';
    if (this.rebalancingState.type === RebalancingType.VAULT) {
      if (this.enzymeState.selectedVault) {
        taker = this.enzymeState.selectedVault.address;
        if (this.enzymeState.useOwnerInsteadOfConnectedWallet) {
          txOrigin = this.enzymeState.selectedVault.owner!;
        }
        encodingType = 'enzyme-vault';
      }
    }

    const swapResponseDto = await lastValueFrom(
      this.httpClient.get<SwapResponseDto>(
        `${this.appConfig.tradingApiBaseUrl}/swap/quote` +
          `?sellToken=${sellEntry.asset.token.address}` +
          `&buyToken=${buyEntry.asset.token.address}` +
          `&sellAmount=${sellEntry.getAmountInTokenUnits().toString()}` +
          `&taker=${taker}` +
          `&txOrigin=${txOrigin}` +
          `&encodingType=${encodingType}`,
      ),
    );

    return RebalancingFactory.createFromSwapDto(swapResponseDto);
  }
}
